import { tokenUtil } from "./TokenUtil";
import { forceLogoutToLogin, getAuthMessageFromResponse } from "./authResume";
import { refreshBrokerBearerToken } from "./brokerAuth";

const throwResponseError = (response) => {
  throw new Error(getAuthMessageFromResponse(response));
};

const AUTH_REFRESH_EXCLUDED_PATHS = [
  "/auth/token",
  "/auth/login",
  "/auth/logout",
  "/auth/refresh-token",
  "/auth/broker/token",
  "/auth/broker/login",
  "/auth/broker/refresh-token",
];

const shouldRefreshRequest = (endPoint = "") =>
  !AUTH_REFRESH_EXCLUDED_PATHS.some((path) => String(endPoint).startsWith(path));

// On a 401, broker sessions get one silent refresh + retry before being logged
// out (the broker bearer token is short-lived). Returns retry metadata so the
// caller can distinguish a refreshed 401 from the original expired-token 401.
const refreshAndRetry = async (reqBody, okStatuses) => {
  if (!shouldRefreshRequest(reqBody?.endPoint)) return null;
  const refreshed = await refreshBrokerBearerToken(reqBody?.token);
  if (!refreshed) return null;
  const token = await tokenUtil.getBearerToken();
  const response = await tokenUtil?.callAPI({ ...reqBody, token });
  return {
    response,
    token,
    ok: okStatuses.includes(response?.status),
  };
};

const handleUnauthorized = async ({ response, reqBody, token, okStatuses }) => {
  const retry = await refreshAndRetry(reqBody, okStatuses);
  if (retry?.ok) return retry.response;
  if (retry?.response && retry.response.status !== 401) {
    throwResponseError(retry.response);
  }
  const authResponse = retry?.response || response;
  forceLogoutToLogin({ response: authResponse, requestToken: retry?.token || token });
  throwResponseError(authResponse);
};

// Pass openApi = true for public/unauthenticated endpoints (e.g. the captcha
// site key). The request then carries NO bearer token, so a stale/expired token
// in localStorage can't make a public call fail with 401 ("works only in
// incognito"), and a 401 here won't force-logout the session.
export const doGET = async function (url, openApi = false) {
  try {
    const token = openApi ? null : await tokenUtil.getBearerToken();
    const reqBody = { reqParam: {}, method: "GET", endPoint: url, token };
    const response = await tokenUtil?.callAPI(reqBody);
    if (response?.status === 200 || response?.status === 204) {
      return response;
    } else if (response?.status === 401 && !openApi) {
      return await handleUnauthorized({ response, reqBody, token, okStatuses: [200, 204] });
    }
    throwResponseError(response);
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const doPOST = async function (url, data, forSupplier) {
  try {
    const token = await tokenUtil.getBearerToken();
    const reqBody = { reqParam: data, method: "POST", endPoint: url, token };
    const response = await tokenUtil?.callAPI(reqBody);
    if (response?.status === 200) {
      return response;
    } else if (response?.status === 401) {
      return await handleUnauthorized({ response, reqBody, token, okStatuses: [200] });
    } else if (response?.status === 413) {
      return response;
    }
    throwResponseError(response);
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const doDELETE = async function (url, data) {
  try {
    const token = await tokenUtil.getBearerToken();
    const reqBody = { reqParam: data, method: "DELETE", endPoint: url, token };
    const response = await tokenUtil?.callAPI(reqBody);
    if (response?.status === 200) {
      return response;
    } else if (response?.status === 401) {
      return await handleUnauthorized({ response, reqBody, token, okStatuses: [200] });
    }
    throwResponseError(response);
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const doPUT = async function (url, data) {
  try {
    const token = await tokenUtil.getBearerToken();
    const reqBody = { reqParam: data, method: "PUT", endPoint: url, token };
    const response = await tokenUtil?.callAPI(reqBody);
    if (response?.status === 200) {
      return response;
    } else if (response?.status === 401) {
      return await handleUnauthorized({ response, reqBody, token, okStatuses: [200] });
    }
    throwResponseError(response);
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const doPATCH = async function (url, data) {
  try {
    const token = await tokenUtil.getBearerToken();
    const reqBody = { reqParam: data, method: "PATCH", endPoint: url, token };
    const response = await tokenUtil?.callAPI(reqBody);
    if (response?.status === 200) {
      return response;
    } else if (response?.status === 401) {
      return await handleUnauthorized({ response, reqBody, token, okStatuses: [200] });
    }
    throwResponseError(response);
  } catch (err) {
    throw new Error(err?.message);
  }
};
